<template>
  <div>
    <div style="display: flex; align-items: center">
      <h2 class="account-card-title">Account Options</h2>
    </div>
    <Card
      class="account-card-info card-style"
      v-if="!editAccountOptions"
    >
      <template #content>
        <div>
          <div
            class="flex"
            style="flex-direction: column; align-items: start"
          >
            <div class="field-title">Password</div>
            <div class="field">*****</div>
          </div>

          <Button
            class="p-button"
            :style="{ marginTop: '10px', width: '100%', color: getContrastYIQ(donorStore.mainColor) }"
            @click="editAccountOptions = true"
            data-test="change-password-button"
          >
            Change Password
          </Button>
        </div>

        <div>
          <div class="field-title">Deactivate Account</div>
          <Button
            class="p-button"
            :style="{ marginTop: '10px', width: '100%', color: getContrastYIQ(donorStore.mainColor) }"
            :disabled="!donorStore.donorInfo.active"
            @click="donorStore.showDeactivateAccount = true"
            data-test="deactivate-account-button"
          >
            Deactivate Account
          </Button>
        </div>
      </template>
    </Card>
    <Card
      class="account-card-edit-info card-style"
      style="display: flex; flex-direction: column; align-items: center; width: 449px"
      v-if="editAccountOptions"
    >
      <template #content>
        <div>
          <div
            v-if="passwordError"
            style="color: red"
            data-test="password-error"
          >
            {{ passwordErrorText }}
          </div>

          <InputText
            class="p-inputtext"
            type="password"
            style="width: 288px; margin-bottom: 10px"
            v-model="currentPassword"
            placeholder="Current Password *"
            data-test="current-password-input"
          />
          <InputText
            class="p-inputtext"
            type="password"
            style="width: 288px; margin-bottom: 10px"
            v-model="newPassword"
            :class="{ 'p-invalid': passwordAlert }"
            placeholder="New Password *"
            data-test="new-password-input"
          />
          <InputText
            class="p-inputtext"
            type="password"
            style="width: 288px"
            v-model="newPasswordConfirm"
            placeholder="Confirm New Password *"
            data-test="confirm-password-input"
          />
          <small
            v-if="passwordAlert"
            class="p-error"
            >{{ passwordAlertText }}</small
          >
        </div>
        <div style="display: flex; justify-content: space-between; width: 100%; padding: 0 20px">
          <Button
            class="p-button-text"
            :style="{ color: donorStore.mainColor }"
            @click="
              editAccountOptions = false;
              donorStore.errorMessage = '';
              donorStore.errorOccurred = false;
            "
            data-test="cancel-button"
          >
            Cancel
          </Button>
          <Button
            class="p-button"
            :style="{ color: getContrastYIQ(donorStore.mainColor) }"
            :loading="updatePasswordLoading"
            @click="updateAccountPassword"
            data-test="save-changes-button"
          >
            Save Changes
          </Button>
        </div>
      </template>
    </Card>
    <Dialog
      v-model:visible="donorStore.showDeactivateAccount"
      header="Deactivate Account"
      :modal="true"
      :closable="false"
      data-test="deactivate-account-modal"
    >
      <p
        v-if="donorStore.errorOccurred"
        class="error-message"
        data-test="deactivate-error-message"
        style="color: red"
      >
        {{ donorStore.errorMessage }}
      </p>

      <div>Are you sure you'd like to deactivate your account?</div>
      <template #footer>
        <Button
          class="p-button-text"
          :style="{ color: donorStore.mainColor }"
          @click="
            donorStore.showDeactivateAccount = false;
            donorStore.errorMessage = '';
            donorStore.errorOccurred = false;
          "
          data-test="deactivate-cancel-button"
        >
          Cancel
        </Button>
        <Button
          class="p-button"
          :style="{ color: getContrastYIQ(donorStore.mainColor) }"
          @click="donorStore.deactivateAccount"
          data-test="deactivate-confirm-button"
        >
          Confirm
        </Button>
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useSnackbarStore } from '@/stores/SnackbarStore.js';
import getContrastYIQ from '@/helpers/getContrastYIQ';

const donorStore = useDonorStore();
const snackbarStore = useSnackbarStore();

const passwordAlert = ref(false);
const passwordAlertText = ref('');
const passwordError = ref(false);
const passwordErrorText = ref('');
const currentPassword = ref('');
const newPassword = ref('');
const newPasswordConfirm = ref('');
const updatePasswordLoading = ref(false);

const editAccountOptions = ref(false);

const updateAccountPassword = async () => {
  if (newPassword.value !== newPasswordConfirm.value) {
    passwordAlert.value = true;
    passwordAlertText.value = 'Passwords do not match';
    return;
  }

  if (newPassword.value.length < 5) {
    passwordAlert.value = true;
    passwordAlertText.value = 'Password must be at least 5 characters long';
    return;
  }

  try {
    updatePasswordLoading.value = true;

    let updateSuccessfully = await donorStore.updateDonorAccountPassword({
      currentPassword: currentPassword.value,
      newPassword: newPassword.value,
    });

    updatePasswordLoading.value = false;

    if (updateSuccessfully != true) {
      passwordError.value = true;
      passwordErrorText.value = updateSuccessfully;
      return;
    }

    editAccountOptions.value = false;

    snackbarStore.show('Your password has been updated!', 'info');
  } catch (error) {
    console.error('Failed to update donor account:', error);
    passwordError.value = true;
    passwordErrorText.value = 'The provided credentials do not match our records.';
  }
};
</script>

<style lang="scss">
.field {
  color: #000;
  text-align: left;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}
.card-info {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}
</style>
